import React, { useState } from 'react';
import { Crown, Check, Star, Zap, Shield, Users, Building2, Database, Briefcase, Code, Globe, Heart } from 'lucide-react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Badge,
  Card,
  CardBody,
  Avatar,
  Divider,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  GridItem,
  useColorModeValue,
  ButtonGroup
} from '@chakra-ui/react';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  period: string;
  features: string[];
  integrations: number;
  isPopular?: boolean;
  icon: React.ReactNode;
}

interface PremiumIntegration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  isPremium: boolean;
}

export function SubscriptionsPage() {
  const [currentPlan, setCurrentPlan] = useState('free');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const bgGradient = useColorModeValue(
    'linear(to-b, purple.50, white)', 
    'linear(to-b, gray.900, gray.800)'
  );
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const plans: SubscriptionPlan[] = [
    {
      id: 'free',
      name: 'Базовый',
      price: 0,
      period: 'месяц',
      features: [
        '5 интеграций',
        '50 сообщений/день',
        'Базовый ИИ помощник',
        'История чатов 7 дней'
      ],
      integrations: 5,
      icon: <Users className="w-6 h-6" />
    },
    {
      id: 'pro',
      name: 'Профессиональный',
      price: billingPeriod === 'monthly' ? 990 : 9900,
      period: billingPeriod === 'monthly' ? 'месяц' : 'год',
      features: [
        '25 интеграций',
        'Безлимитные сообщения',
        'Продвинутый ИИ помощник',
        'История чатов 90 дней',
        'Голосовые звонки',
        'Приоритетная поддержка'
      ],
      integrations: 25,
      isPopular: true,
      icon: <Zap className="w-6 h-6" />
    },
    {
      id: 'enterprise',
      name: 'Корпоративный',
      price: billingPeriod === 'monthly' ? 2990 : 29900,
      period: billingPeriod === 'monthly' ? 'месяц' : 'год',
      features: [
        'Все интеграции',
        'Безлимитные сообщения',
        'Премиум ИИ помощник',
        'Безлимитная история',
        'Голосовые звонки',
        'Кастомные интеграции',
        '24/7 поддержка',
        'API доступ'
      ],
      integrations: 999,
      icon: <Building2 className="w-6 h-6" />
    }
  ];

  const premiumIntegrations: PremiumIntegration[] = [
    {
      id: 'microsoft-dynamics',
      name: 'Microsoft Dynamics 365',
      description: 'CRM и ERP решение от Microsoft',
      icon: <Database className="w-8 h-8 text-blue-600" />,
      category: 'CRM/ERP',
      isPremium: true
    },
    {
      id: 'oracle-erp',
      name: 'Oracle ERP Cloud',
      description: 'Облачная ERP система Oracle',
      icon: <Database className="w-8 h-8 text-red-600" />,
      category: 'ERP',
      isPremium: true
    },
    {
      id: 'tableau',
      name: 'Tableau',
      description: 'Платформа для визуализации данных',
      icon: <Briefcase className="w-8 h-8 text-orange-600" />,
      category: 'Analytics',
      isPremium: true
    },
    {
      id: 'power-bi',
      name: 'Microsoft Power BI',
      description: 'Бизнес-аналитика и визуализация',
      icon: <Briefcase className="w-8 h-8 text-yellow-600" />,
      category: 'Analytics',
      isPremium: true
    },
    {
      id: 'zendesk',
      name: 'Zendesk',
      description: 'Платформа для поддержки клиентов',
      icon: <Heart className="w-8 h-8 text-green-600" />,
      category: 'Support',
      isPremium: true
    },
    {
      id: 'confluence',
      name: 'Confluence',
      description: 'Корпоративная wiki система',
      icon: <Globe className="w-8 h-8 text-blue-500" />,
      category: 'Knowledge',
      isPremium: true
    },
    {
      id: 'gitlab',
      name: 'GitLab',
      description: 'DevOps платформа',
      icon: <Code className="w-8 h-8 text-orange-500" />,
      category: 'DevOps',
      isPremium: true
    },
    {
      id: 'jenkins',
      name: 'Jenkins',
      description: 'CI/CD автоматизация',
      icon: <Code className="w-8 h-8 text-gray-600" />,
      category: 'DevOps',
      isPremium: true
    }
  ];

  const handleUpgrade = (planId: string) => {
    // Здесь была бы логика обновления подписки
    console.log('Upgrading to plan:', planId);
  };

  return (
    <Box minH="100vh" bgGradient={bgGradient}>
      <Box maxW="md" mx="auto" p={4} pb={20}>
        {/* Header */}
        <VStack spacing={4} textAlign="center" mb={8} pt={8}>
          <Avatar
            size="lg"
            bg="linear-gradient(135deg, #9333ea, #ec4899)"
            icon={<Icon as={Crown} w={8} h={8} color="white" />}
          />
          <VStack spacing={2}>
            <Heading size="lg" color={textColor}>Подписки</Heading>
            <Text color={mutedColor}>Выберите подходящий план</Text>
          </VStack>
        </VStack>

        <Tabs defaultIndex={0} colorScheme="purple">
          <TabList gridTemplateColumns="1fr 1fr" mb={6} bg={cardBg} borderRadius="md">
            <Tab>Планы</Tab>
            <Tab>Интеграции</Tab>
          </TabList>

          <TabPanels>
            <TabPanel p={0}>
              {/* Billing Period Toggle */}
              <HStack justify="center" mb={6}>
                <ButtonGroup 
                  bg={useColorModeValue('gray.100', 'gray.700')} 
                  p={1} 
                  borderRadius="lg" 
                  isAttached
                >
                  <Button
                    size="sm"
                    variant={billingPeriod === 'monthly' ? 'solid' : 'ghost'}
                    colorScheme={billingPeriod === 'monthly' ? 'purple' : 'gray'}
                    onClick={() => setBillingPeriod('monthly')}
                  >
                    Ежемесячно
                  </Button>
                  <Button
                    size="sm"
                    variant={billingPeriod === 'yearly' ? 'solid' : 'ghost'}
                    colorScheme={billingPeriod === 'yearly' ? 'purple' : 'gray'}
                    onClick={() => setBillingPeriod('yearly')}
                    rightIcon={<Badge colorScheme="green" fontSize="xs">-17%</Badge>}
                  >
                    Ежегодно
                  </Button>
                </ButtonGroup>
              </HStack>

              {/* Plans */}
              <VStack spacing={4}>
                {plans.map((plan) => (
                  <Box key={plan.id} position="relative" w="full">
                    {plan.isPopular && (
                      <Badge
                        position="absolute"
                        top={-2}
                        left="50%"
                        transform="translateX(-50%)"
                        bgGradient="linear(to-r, purple.500, pink.600)"
                        color="white"
                        zIndex={1}
                      >
                        <Icon as={Star} w={3} h={3} mr={1} />
                        Популярный
                      </Badge>
                    )}
                    
                    <Card 
                      bg={cardBg}
                      borderWidth={2}
                      borderColor={
                        plan.isPopular 
                          ? 'purple.200' 
                          : currentPlan === plan.id 
                            ? 'purple.300' 
                            : 'transparent'
                      }
                      shadow={plan.isPopular ? 'lg' : 'md'}
                    >
                      <CardBody p={4}>
                        <HStack justify="space-between" mb={4}>
                          <HStack spacing={3}>
                            <Avatar
                              size="sm"
                              bg={
                                plan.isPopular 
                                  ? 'linear-gradient(135deg, #9333ea, #ec4899)' 
                                  : 'gray.200'
                              }
                              color={plan.isPopular ? 'white' : 'gray.600'}
                              icon={plan.icon}
                            />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium" color={textColor}>
                                {plan.name}
                              </Text>
                              <Text fontSize="sm" color={mutedColor}>
                                {plan.integrations === 999 ? 'Все' : plan.integrations} интеграций
                              </Text>
                            </VStack>
                          </HStack>
                          <VStack align="end" spacing={0}>
                            <Text fontSize="2xl" fontWeight="semibold" color={textColor}>
                              {plan.price === 0 ? 'Бесплатно' : `₽${plan.price.toLocaleString()}`}
                            </Text>
                            {plan.price > 0 && (
                              <Text fontSize="sm" color={mutedColor}>
                                /{plan.period}
                              </Text>
                            )}
                          </VStack>
                        </HStack>

                        <VStack spacing={2} mb={4} align="stretch">
                          {plan.features.map((feature, index) => (
                            <HStack key={index} spacing={2}>
                              <Icon as={Check} w={4} h={4} color="green.500" />
                              <Text fontSize="sm" color={textColor}>{feature}</Text>
                            </HStack>
                          ))}
                        </VStack>

                        <Button 
                          w="full"
                          colorScheme={currentPlan === plan.id ? 'gray' : 'purple'}
                          variant={currentPlan === plan.id ? 'solid' : 'solid'}
                          isDisabled={currentPlan === plan.id}
                          onClick={() => handleUpgrade(plan.id)}
                        >
                          {currentPlan === plan.id 
                            ? 'Текущий план' 
                            : plan.id === 'free' 
                              ? 'Текущий план' 
                              : 'Выбрать план'
                          }
                        </Button>
                      </CardBody>
                    </Card>
                  </Box>
                ))}
              </VStack>
            </TabPanel>

            <TabPanel p={0}>
              <VStack spacing={6} mb={6}>
                <VStack spacing={2}>
                  <Heading size="md" color={textColor}>Премиум интеграции</Heading>
                  <Text fontSize="sm" color={mutedColor} textAlign="center">
                    Доступны в планах Профессиональный и Корпоративный
                  </Text>
                </VStack>
              </VStack>

              <VStack spacing={4}>
                {premiumIntegrations.map((integration) => (
                  <Card key={integration.id} bg={cardBg} opacity={0.75} w="full">
                    <CardBody p={4}>
                      <HStack align="start" spacing={4}>
                        <Box flexShrink={0}>
                          {integration.icon}
                        </Box>
                        <VStack align="start" flex={1} spacing={2}>
                          <HStack justify="space-between" w="full">
                            <Text fontWeight="medium" color={textColor}>
                              {integration.name}
                            </Text>
                            <HStack spacing={2}>
                              <Icon as={Crown} w={4} h={4} color="yellow.500" />
                              <Badge variant="outline" fontSize="xs">
                                Premium
                              </Badge>
                            </HStack>
                          </HStack>
                          <Text fontSize="sm" color={mutedColor} mb={2}>
                            {integration.description}
                          </Text>
                          <Badge colorScheme="gray" fontSize="xs">
                            {integration.category}
                          </Badge>
                        </VStack>
                      </HStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>

              <Box mt={8} textAlign="center">
                <Card 
                  bg="linear-gradient(135deg, #f3e8ff, #fce7f3)"
                  borderColor="purple.200"
                >
                  <CardBody p={6}>
                    <VStack spacing={4}>
                      <Icon as={Crown} w={12} h={12} color="purple.600" />
                      <Heading size="sm" color={textColor}>
                        Хотите больше интеграций?
                      </Heading>
                      <Text fontSize="sm" color={mutedColor} textAlign="center">
                        Обновите план до Профессионального или Корпоративного
                      </Text>
                      <Button 
                        bgGradient="linear(to-r, purple.500, pink.600)"
                        color="white"
                        _hover={{ bgGradient: "linear(to-r, purple.600, pink.700)" }}
                      >
                        Обновить план
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}